import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { authApi } from '../services/api';
import './auth/LoginDesign.css';

const getErrorCode = (err) => {
  const data = err.response?.data || {};
  return data.code || data.errorCode || data.error?.code || '';
};

const getErrorMessage = (err) => {
  const data = err.response?.data || {};
  return data.message || data.error?.message || 'This verification link is invalid or has expired.';
};

const VerifyEmail = () => {
  const location = useLocation();
  const token = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('token') || params.get('verificationToken') || params.get('verification_token') || '';
  }, [location.search]);

  const [state, setState] = useState({
    loading: true,
    result: null,
    errorCode: '',
    errorMessage: '',
  });

  useEffect(() => {
    let cancelled = false;

    const verify = async () => {
      if (!token) {
        setState({
          loading: false,
          result: null,
          errorCode: 'EMAIL_VERIFICATION_MISSING',
          errorMessage: 'This verification link is missing its token.',
        });
        return;
      }

      setState({ loading: true, result: null, errorCode: '', errorMessage: '' });

      try {
        const response = await authApi.verifyEmail(token);
        if (cancelled) return;
        setState({
          loading: false,
          result: response.data?.data || response.data || {},
          errorCode: '',
          errorMessage: '',
        });
      } catch (err) {
        if (cancelled) return;
        setState({
          loading: false,
          result: null,
          errorCode: getErrorCode(err) || 'EMAIL_VERIFICATION_INVALID',
          errorMessage: getErrorMessage(err),
        });
      }
    };

    verify();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const verified = Boolean(state.result?.verified);
  const canSignIn = Boolean(state.result?.canSignIn) || state.result?.nextAction === 'SIGN_IN';
  const isPending = state.result?.nextAction === 'WAIT_FOR_ADMIN_APPROVAL' || state.result?.status === 'pending';

  let title = 'Checking your email link';
  let body = 'Please wait while we verify your email address.';

  if (!state.loading && verified && canSignIn) {
    title = 'Your email has been verified';
    body = state.result?.message || 'Your email is verified. You can now log in.';
  } else if (!state.loading && verified && isPending) {
    title = 'Your email has been verified';
    body = state.result?.message || 'Your email is verified. An administrator will review your account shortly. We will email you when you can log in.';
  } else if (!state.loading && state.errorCode) {
    title = state.errorCode === 'EMAIL_VERIFICATION_MISSING'
      ? 'Verification link missing'
      : 'This verification link cannot be used';
    body = state.errorMessage;
  }

  return (
    <div className="login-page verify-email-page">
      <header className="login-header">
        <div className="login-header-inner">
          <Link to="/" className="login-logo">
            Studio<span className="login-logo-accent">AEye</span>
          </Link>
          <Link to="/login" className="login-back-link">
            Back to login
          </Link>
        </div>
      </header>

      <main className="login-main">
        <section className="login-card verify-email-card" aria-live="polite">
          <div className="login-heading">
            <p className="verify-email-kicker">Email verification</p>
            <h1 className="login-title">{title}</h1>
            <p className="login-subtitle">{body}</p>
          </div>

          {state.loading ? (
            <div className="login-alert login-alert-success" data-testid="verify-email-loading">
              <span className="login-spinner" />
              Verifying email address...
            </div>
          ) : null}

          {!state.loading && verified ? (
            <div className="login-alert login-alert-success" data-testid="verify-email-success">
              {isPending
                ? 'Email verified. Waiting for admin approval.'
                : 'Email verified. You can now log in.'}
            </div>
          ) : null}

          {!state.loading && state.errorCode ? (
            <div role="alert" className="login-alert login-alert-error" data-testid="verify-email-error">
              {state.errorCode === 'EMAIL_VERIFICATION_INVALID'
                ? 'This link may be invalid, expired, or already used.'
                : body}
            </div>
          ) : null}

          <div className="verify-email-actions">
            {verified && canSignIn ? (
              <Link to="/login" className="login-submit verify-email-primary-link">
                Log in
              </Link>
            ) : null}

            {verified && isPending ? (
              <Link to="/" className="login-submit verify-email-primary-link">
                Return home
              </Link>
            ) : null}

            {!state.loading && state.errorCode ? (
              <>
                <Link to="/register" className="login-submit verify-email-primary-link">
                  Request a new verification email
                </Link>
                <Link to="/login" className="login-link verify-email-secondary-link">
                  Back to login
                </Link>
              </>
            ) : null}
          </div>

          <p className="login-footer">© StudioAEye {new Date().getFullYear()}</p>
        </section>
      </main>
    </div>
  );
};

export default VerifyEmail;
